import { Association } from "./types";
import { calculateAmountPerAssociation } from "./utils";

/** Amounts the auto-split last wrote, keyed by association target. */
export type AutoAllocatedAmounts = Map<string, number>;

export function associationKey(
  association: Pick<Association, "reportId" | "facilityId" | "submissionId">
): string {
  return `${association.reportId ?? ""}|${association.facilityId ?? ""}|${association.submissionId ?? ""}`;
}

/**
 * Splits the payment amount evenly across associations whose amounts were not
 * typed by hand: every row still holds what the last auto-split wrote for it,
 * or is a new row at 0. A row staff zeroed out after an auto-split counts as
 * hand-typed. A single association always takes the full amount. Returns
 * undefined when a row was hand-edited or the split would change nothing, so
 * callers can run this on every change without clobbering staff input.
 */
export function allocateUntouchedAmounts({
  amount,
  associations,
  autoAllocated,
}: {
  amount: number | null | undefined;
  associations: Association[];
  autoAllocated: AutoAllocatedAmounts;
}): { associations: Association[]; autoAllocated: AutoAllocatedAmounts } | undefined {
  if (associations.length === 0) {
    return undefined;
  }

  const untouched =
    associations.length === 1 ||
    associations.every((association) => {
      const key = associationKey(association);
      if (autoAllocated.has(key)) {
        return autoAllocated.get(key) === association.amount;
      }
      return association.amount === 0;
    });
  if (!untouched) {
    return undefined;
  }

  const amounts = calculateAmountPerAssociation(amount, associations.length);
  if (associations.every((association, index) => association.amount === amounts[index])) {
    return undefined;
  }

  const next = associations.map((association, index) => ({
    ...association,
    amount: amounts[index],
  }));
  return {
    associations: next,
    autoAllocated: new Map(
      next.map((association) => [associationKey(association), association.amount])
    ),
  };
}
